import { useEffect, useState, type MutableRefObject } from "react";
import { useTranslation } from "react-i18next";
import {
  UPLOAD_POLICIES,
  UPLOAD_PURPOSE_MARKETPLACE_PARTNER_PREFLIGHT,
  validateUploadMeta,
} from "@nexus/contracts";
import { Button, Icon } from "@nexus/ui";
import { toast } from "sonner";

import { useAuth } from "@/features/auth/hooks/useAuth";
import { runPartnerPreflightUploadFlow } from "@/features/marketplace/api/partner-preflight-upload.api";
import { getMarketplacePartnerPreflightUploadConstraints } from "@/features/marketplace/lib/marketplace-partner-preflight-upload-constraints";
import { resolvePartnerPreflightContentType } from "@/features/marketplace/lib/resolve-partner-preflight-content-type";
import { PartnerVideoConfirmDialog } from "@/features/marketplace/partner/components/PartnerVideoConfirmDialog";
import {
  PartnerPreflightUploadProgress,
  type PartnerPreflightSubmitPhase,
} from "@/features/marketplace/partner/components/PartnerPreflightUploadProgress";
import type {
  MarketplaceFulfillmentStatus,
  MarketplaceOrder,
  OrderScriptRow,
  PartnerOrder,
} from "@/features/marketplace/types/marketplace.types";
import { UploadDropzone } from "@/features/preflight/components/UploadDropzone/UploadDropzone";
import { getUserFacingApiMessage } from "@/lib/api-client";
import { usePartnerVoiceoverCheck, useUploadPartnerPreflight } from "@/lib/queries";

const PARTNER_VIDEO_POLICY =
  UPLOAD_POLICIES[UPLOAD_PURPOSE_MARKETPLACE_PARTNER_PREFLIGHT];
const PARTNER_PREFLIGHT_UPLOAD =
  getMarketplacePartnerPreflightUploadConstraints();

export interface PartnerPreflightSuccessState {
  scriptReviewId: string;
  scriptTitle: string;
  clientName: string;
  submissionStatus: MarketplaceFulfillmentStatus;
  /** A hook video landed but the line still waits on other hooks, so the client is not notified yet. */
  isPartialHookDelivery?: boolean;
}

const SET_SUBMITTED_STATUS = "SUBMITTED_FOR_FEEDBACK" satisfies MarketplaceFulfillmentStatus;

export type PartnerPreflightRowPhase = PartnerPreflightSubmitPhase | "idle";

export function isPartnerUploadCancellablePhase(
  phase: PartnerPreflightRowPhase,
): boolean {
  return (
    phase === "preparing" || phase === "uploading" || phase === "confirming"
  );
}

/**
 * One video upload: pick → upload → voiceover check → confirm → submit.
 * Alternate hooks pass their `hookKey`; the primary hook (and a one-hook line) omits it.
 */
export function PartnerVideoUploadSlot({
  order,
  script,
  title,
  hookKey,
  isLocked = false,
  abortRef,
  onBusyChange,
  onUploadSuccess,
}: {
  order: PartnerOrder;
  script: OrderScriptRow;
  title: string;
  hookKey?: string;
  /** Another slot on the same line is mid-upload. */
  isLocked?: boolean;
  abortRef: MutableRefObject<AbortController | null>;
  onBusyChange?: (isBusy: boolean) => void;
  onUploadSuccess: (state: PartnerPreflightSuccessState) => void;
}) {
  const { t } = useTranslation();
  const activeTenantId = useAuth((state) => state.activeTenantId);
  const uploadPreflight = useUploadPartnerPreflight();
  const voiceoverCheck = usePartnerVoiceoverCheck();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [pendingUploadId, setPendingUploadId] = useState<string | null>(null);
  const [fileRejection, setFileRejection] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadPhase, setUploadPhase] =
    useState<PartnerPreflightRowPhase>("idle");
  const isSubmitting = uploading || uploadPreflight.isPending;
  const isBusy = isSubmitting || pendingUploadId !== null;
  const hookTarget = hookKey ? { hookKey } : {};

  useEffect(() => {
    onBusyChange?.(isBusy);
  }, [isBusy, onBusyChange]);

  const completeSubmit = (updatedOrder: MarketplaceOrder) => {
    if (useAuth.getState().activeTenantId !== activeTenantId) return;
    const updatedScript = updatedOrder.scripts.find(
      (candidate) => candidate.id === script.id,
    );
    const scriptReviewId = hookKey
      ? updatedScript?.hookDeliveries?.find(
          (delivery) => delivery.hookKey === hookKey,
        )?.scriptReviewId
      : updatedScript?.scriptReviewId;
    if (updatedScript && scriptReviewId) {
      onUploadSuccess({
        scriptReviewId,
        scriptTitle: title,
        clientName: order.tenant.name,
        submissionStatus: updatedScript.fulfillmentStatus,
        isPartialHookDelivery:
          (updatedScript.hookDeliveries?.length ?? 0) > 0 &&
          updatedScript.fulfillmentStatus !== SET_SUBMITTED_STATUS,
      });
    }
    setSelectedFile(null);
  };

  const handleFiles = (files: FileList | File[]) => {
    const nextFile = files[0];
    if (!nextFile) return;
    const contentType = resolvePartnerPreflightContentType(
      nextFile,
      PARTNER_VIDEO_POLICY,
    );
    const validation = validateUploadMeta(
      { contentType, sizeBytes: nextFile.size },
      PARTNER_VIDEO_POLICY,
    );
    if (!validation.ok) {
      setSelectedFile(null);
      setFileRejection(
        t("marketplace.partner.fileRejected", {
          fileName: nextFile.name,
          reason: validation.reason.replace(/_/g, " ").toLowerCase(),
          defaultValue: "{{fileName}} — {{reason}}",
        }),
      );
      return;
    }
    setSelectedFile(nextFile);
    setFileRejection(null);
  };

  const handleSubmit = async () => {
    if (!selectedFile || !activeTenantId) return;
    const controller = new AbortController();
    abortRef.current = controller;
    setUploading(true);
    setIsCancelling(false);
    setUploadProgress(0);
    setUploadPhase("preparing");
    try {
      const upload = await runPartnerPreflightUploadFlow({
        tenantId: activeTenantId,
        file: selectedFile,
        signal: controller.signal,
        onProgress: (percent) => {
          setUploadProgress(percent);
          if (percent >= 100) setUploadPhase("confirming");
        },
        onPhase: setUploadPhase,
      });
      resetUploadState();
      setPendingUploadId(upload.uploadId);
      voiceoverCheck.mutate({
        partnerTenantId: activeTenantId,
        orderId: order.id,
        orderScriptId: script.id,
        uploadId: upload.uploadId,
        ...hookTarget,
      });
    } catch (error) {
      if (!(error instanceof DOMException && error.name === "AbortError")) {
        toast.error(
          getUserFacingApiMessage(error) ||
            t(
              "marketplace.partner.preflightUploadError",
              "Couldn't submit preflight",
            ),
        );
      }
      resetUploadState();
    }
  };

  const closeConfirm = () => {
    setPendingUploadId(null);
    voiceoverCheck.reset();
  };

  const handleConfirmUpload = () => {
    if (!pendingUploadId || !activeTenantId) return;
    const uploadId = pendingUploadId;
    closeConfirm();
    setUploadPhase("submitting");
    uploadPreflight.mutate(
      {
        partnerTenantId: activeTenantId,
        orderId: order.id,
        orderScriptId: script.id,
        uploadId,
        ...hookTarget,
      },
      {
        onSuccess: completeSubmit,
        onSettled: resetUploadState,
      },
    );
  };

  const resetUploadState = () => {
    abortRef.current = null;
    setUploading(false);
    setIsCancelling(false);
    setUploadProgress(0);
    setUploadPhase("idle");
  };

  return (
    <>
      <UploadDropzone
        onFiles={handleFiles}
        accept={PARTNER_PREFLIGHT_UPLOAD.accept}
        multiple={false}
        disabled={isSubmitting || isLocked}
        browseTrigger="button"
        className="py-5"
        titleText={t(
          "marketplace.partner.dropzoneTitle",
          "Drag, drop or paste video",
        )}
        constraintsText={t("marketplace.partner.dropzoneConstraints", {
          formats: PARTNER_PREFLIGHT_UPLOAD.formats,
          videoMax: PARTNER_PREFLIGHT_UPLOAD.videoMax,
          defaultValue: "{{formats}} · Videos up to {{videoMax}}",
        })}
        buttonLabel={
          selectedFile
            ? t("marketplace.partner.dropzoneReplace", "Replace file")
            : t("marketplace.partner.dropzoneBrowse", "Browse files")
        }
      />
      {fileRejection ? (
        <p className="flex items-center gap-1.5 text-xs text-error">
          <Icon name="alert-circle" size="xs" color="inherit" aria-hidden />
          {fileRejection}
        </p>
      ) : null}
      {selectedFile && !isSubmitting ? (
        <div className="flex min-h-11 items-center justify-between gap-3 rounded-md bg-muted/40 px-3 py-2">
          <span className="min-w-0 truncate text-xs text-foreground">
            {selectedFile.name}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              setSelectedFile(null);
            }}
          >
            {t("marketplace.partner.upload.remove", "Remove")}
          </Button>
        </div>
      ) : null}
      {isSubmitting && uploadPhase !== "idle" ? (
        <PartnerPreflightUploadProgress
          phase={uploadPhase}
          progress={uploadProgress}
          fileName={selectedFile?.name}
        />
      ) : null}
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          size="sm"
          disabled={!selectedFile || isSubmitting || isLocked}
          onClick={() => void handleSubmit()}
        >
          {isSubmitting
            ? t("marketplace.partner.preflightUploading", "Submitting…")
            : t(
                "marketplace.partner.preflightUpload",
                "Submit work for review",
              )}
        </Button>
        {isSubmitting && isPartnerUploadCancellablePhase(uploadPhase) ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={isCancelling}
            onClick={() => {
              setIsCancelling(true);
              abortRef.current?.abort();
            }}
          >
            {isCancelling
              ? t("marketplace.partner.upload.cancelling", "Cancelling…")
              : t("marketplace.partner.upload.cancel", "Cancel upload")}
          </Button>
        ) : null}
      </div>
      <PartnerVideoConfirmDialog
        open={pendingUploadId !== null}
        file={selectedFile}
        scriptTitle={title}
        isCheckingVoiceover={voiceoverCheck.isPending}
        voiceoverMismatch={voiceoverCheck.data ?? null}
        onConfirm={handleConfirmUpload}
        onCancel={closeConfirm}
      />
    </>
  );
}
